import { useFormik } from "formik";
import {
  Button,
  Divider,
  Stack,
  Typography,
  TextField,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Switch,
  FormControlLabel,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import { MonitoringCreateFormSchema } from "./schema";
import MonitoringIcon from "./monitoring-icon.svg";
import { Prefill } from "../types";
import APICompanyMatchProfile from "../../../../types/APICompanyMatchProfile";
import {
  APIMonitoringEnrollmentInput,
  APIMonitoringEnrollment,
  APIMonitorType,
} from "../../../../types/APIMonitoringEnrollment";

export type FormState = ReturnType<
  typeof MonitoringCreateFormSchema.validateSync
>;

export interface Props {
  onCancel: () => void;
  onSuccess: (
    domain: string,
    frequencyDays: number,
    nextRun?: string | null | undefined
  ) => void;
  onError: (domain: string, reason?: string, errors?: string[]) => void;
  onCreateMonitor: (
    formData: APIMonitoringEnrollmentInput
  ) => Promise<APIMonitoringEnrollment>;
  prefill?: Partial<Prefill>;
  matchProfiles: APICompanyMatchProfile[];
  withPeople?: boolean;
}

export default function MonitorCreateForm({
  onCancel,
  onSuccess,
  onError,
  onCreateMonitor,
  prefill,
  matchProfiles,
  withPeople = false,
}: Props) {
  const formik = useFormik({
    initialValues: {
      domain: "",
      closure_selected: true,
      frequency_days: 30,
      content_selected: true,
      sentiment_selected: true,
      profile_selected: true,
      connected_entities_selected: true,
      people_selected: withPeople ? true : false,
      external_ref_id: "",
      profile_id: "",
      ...(prefill || {}),
    },
    onSubmit: async (values: FormState, { resetForm }) => {
      try {
        // Formik will not automatically honor yup's value transforms which we need
        // to potentially clean and extract the domain/hostname from a url
        const transformedValues = MonitoringCreateFormSchema.cast(values);

        const creationInput: any = {
          domain: transformedValues.domain,
          monitors: [],
        };

        creationInput.domain = transformedValues.domain;
        if (transformedValues.external_ref_id) {
          creationInput.external_ref_id = transformedValues.external_ref_id;
        }

        if (transformedValues.profile_id) {
          creationInput.profile_id = transformedValues.profile_id;
        }

        if (transformedValues.closure_selected) {
          creationInput.monitors.push({
            type: APIMonitorType.BUSINESS_CLOSURE,
            frequency: { days: transformedValues.frequency_days },
          });
        }

        if (transformedValues.content_selected) {
          creationInput.monitors.push({
            type: APIMonitorType.WEBSITE_CONTENT,
            frequency: { days: transformedValues.frequency_days },
          });
        }

        if (transformedValues.sentiment_selected) {
          creationInput.monitors.push({
            type: APIMonitorType.SENTIMENT_ANALYSIS,
            frequency: { days: transformedValues.frequency_days },
          });
        }

        if (transformedValues.profile_selected) {
          creationInput.monitors.push({
            type: APIMonitorType.BUSINESS_PROFILE,
            frequency: { days: transformedValues.frequency_days },
          });
        }
        if (transformedValues.connected_entities_selected) {
          creationInput.monitors.push({
            type: APIMonitorType.CONNECTED_ENTITIES,
            frequency: { days: transformedValues.frequency_days },
          });
        }

        if (transformedValues.people_selected) {
          creationInput.monitors.push({
            type: APIMonitorType.PEOPLE,
            frequency: { days: transformedValues.frequency_days },
          });
        }

        const result = await onCreateMonitor(creationInput);
        resetForm();
        // Report the canonical target the API settled on, not what was typed.
        // The API decides how much of a URL survives -- it keeps the page path
        // for a platform URL ("instagram.com/acme") and drops it for an
        // ordinary domain ("example.com") -- and only it knows which hosts are
        // platform domains, so this is the first point the answer exists.
        onSuccess(
          result.domain,
          transformedValues.frequency_days,
          result.next_run
        );
      } catch (e) {
        let description = "An unknown error";
        let errors = [];
        try {
          if (e instanceof Response) {
            const j = await e.json();
            description = j?.message || JSON.stringify(j);
            errors =
              j?.errors.flatMap((error: any) => error.errors || []) || [];
          }
        } catch (e) {
          // pass
        }

        onError(values.domain, description, errors);
      }
    },
    validationSchema: MonitoringCreateFormSchema,
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Stack
        sx={{
          alignItems: "center",
          mb: "2em",
        }}
      >
        <img src={MonitoringIcon} width={100} alt="" />
        <Typography>
          Enroll a merchant in
          <br />
          TrueBiz monitoring
        </Typography>
      </Stack>
      <Stack
        sx={{
          gap: "1em",
        }}
      >
        <FormControl>
          <TextField
            fullWidth
            name="domain"
            label="Domain (required)"
            required
            onChange={formik.handleChange}
            value={formik.values.domain}
            error={!!(formik.touched.domain && formik.errors.domain)}
            autoFocus
          />
          {formik.errors.domain && formik.touched.domain && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {formik.errors.domain}
            </Alert>
          )}
        </FormControl>

        {formik.dirty && (formik.errors as any)?.monitor_selection && (
          <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
            {(formik.errors as any)?.monitor_selection}
          </Alert>
        )}
        <Typography variant="h5">Alert me for:</Typography>

        <Box
          sx={{
            pl: 1,
          }}
        >
          <FormControl fullWidth sx={{ mb: 1 }}>
            <FormControlLabel
              label="Industry classification"
              control={
                <Switch
                  name="profile_selected"
                  checked={formik.values.profile_selected}
                  onChange={formik.handleChange}
                />
              }
            />
          </FormControl>

          <FormControl fullWidth sx={{ mb: 1 }}>
            <FormControlLabel
              label="High risk content"
              control={
                <Switch
                  name="content_selected"
                  checked={formik.values.content_selected}
                  onChange={formik.handleChange}
                />
              }
            />
          </FormControl>

          <FormControl fullWidth sx={{ mb: 1 }}>
            <FormControlLabel
              label="Business closure"
              control={
                <Switch
                  name="closure_selected"
                  checked={formik.values.closure_selected}
                  onChange={formik.handleChange}
                />
              }
            />
          </FormControl>

          <FormControl fullWidth sx={{ mb: 1 }}>
            <FormControlLabel
              label="Review sentiment & adverse media"
              control={
                <Switch
                  name="sentiment_selected"
                  checked={formik.values.sentiment_selected}
                  onChange={formik.handleChange}
                />
              }
            />
          </FormControl>

          <FormControl fullWidth sx={{ mb: 1 }}>
            <FormControlLabel
              label="Connected entities"
              control={
                <Switch
                  name="connected_entities_selected"
                  checked={formik.values.connected_entities_selected}
                  onChange={formik.handleChange}
                />
              }
            />
          </FormControl>

          {withPeople && (
            <FormControl fullWidth sx={{ mb: 1 }}>
              <FormControlLabel
                label="Connected people"
                control={
                  <Switch
                    name="people_selected"
                    checked={formik.values.people_selected}
                    onChange={formik.handleChange}
                  />
                }
              />
            </FormControl>
          )}

          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Scan every</InputLabel>
              <Select
                size="small"
                value={formik.values.frequency_days}
                label="Scan every"
                onChange={formik.handleChange}
                name="frequency_days"
              >
                <MenuItem value={7}>7 days</MenuItem>
                <MenuItem value={30}>30 days</MenuItem>
                <MenuItem value={90}>90 days</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Divider />
        <details>
          <summary style={{ cursor: "pointer" }}>Advanced Settings</summary>
          <Stack
            sx={{
              pt: 5,
              gap: 2,
            }}
          >
            {matchProfiles?.length > 0 && (
              <FormControl sx={{ width: "100%" }}>
                <InputLabel
                  id="match-profile-label"
                  shrink
                  sx={{ background: "white" }}
                >
                  Risk Profile
                </InputLabel>
                <Select
                  labelId="match-profile-label"
                  label="Risk Profile"
                  id="match-profile-select"
                  defaultValue={formik.initialValues.profile_id}
                  value={
                    formik.values.profile_id ? formik.values.profile_id : ""
                  }
                  onChange={formik.handleChange}
                  name="profile_id"
                  displayEmpty
                >
                  <MenuItem value={""}>TrueBiz Default</MenuItem>
                  {matchProfiles.map((profile) => {
                    if (profile.id) {
                      return (
                        <MenuItem key={profile.id} value={profile.id}>
                          {profile.name}
                        </MenuItem>
                      );
                    }
                    return null;
                  })}
                </Select>
                {formik.errors.profile_id && formik.touched.profile_id && (
                  <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                    {formik.errors.profile_id}
                  </Alert>
                )}
              </FormControl>
            )}

            <FormControl>
              <TextField
                fullWidth
                name="external_ref_id"
                label="External Reference ID"
                onChange={formik.handleChange}
                value={formik.values.external_ref_id}
                error={
                  !!(
                    formik.touched.external_ref_id &&
                    formik.errors.external_ref_id
                  )
                }
              />
              {formik.errors.external_ref_id &&
                formik.touched.external_ref_id && (
                  <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                    {formik.errors.external_ref_id}
                  </Alert>
                )}
            </FormControl>
          </Stack>
        </details>
      </Stack>
      <div
        style={{
          display: "flex",
          justifyContent: "end",
          width: "100%",
          gap: "1.5em",
          marginTop: "1.5em",
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            formik.resetForm();
            onCancel();
          }}
          disabled={formik.isSubmitting}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          type="submit"
          loading={formik.isSubmitting}
        >
          Enroll Merchant
        </LoadingButton>
      </div>
    </form>
  );
}
